import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { type Clip } from '@air/api/types';
import { type SelectableItem } from '@air/redux-selected-items';

import { usePrivateWorkspaceAgentClipsListByIds } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentClipsListByIds';
import {
  resolveSelectableItemsForClipIds,
  stableListByIdsClipIds,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentClipIdsScope';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { isSelectableGalleryItem } from '~/store/selectedItems/types';
import { type AirState } from '~/store/types';
import { useAirSelector } from '~/utils/ReduxUtils';

const clipFromSelectableItem = (item: SelectableItem): Clip | null => {
  if (!isSelectableGalleryItem(item) || (item.type !== 'asset' && item.type !== 'file')) {
    return null;
  }

  return item.item;
};

/** Clips for agent scope UI (thumbnails, labels) in `clipIds` order — Redux, then `listByIds`. */
export const useWorkspaceAgentClipsForClipIds = (clipIds: Clip['id'][]): Clip[] => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const selectableItems = useAirSelector(
    (state: AirState) => resolveSelectableItemsForClipIds(state, clipIds),
    shallowEqual,
  );

  const reduxClipsById = useMemo(() => {
    const clipsById = new Map<string, Clip>();

    for (const item of selectableItems) {
      const clip = clipFromSelectableItem(item);

      if (clip) {
        clipsById.set(clip.id, clip);
      }
    }

    return clipsById;
  }, [selectableItems]);

  const listByIdsClipIds = useMemo(
    () => stableListByIdsClipIds(clipIds.filter((clipId) => !reduxClipsById.has(clipId))),
    [clipIds, reduxClipsById],
  );

  const { data: listByIdsClipList } = usePrivateWorkspaceAgentClipsListByIds({
    clipIds: listByIdsClipIds,
    workspaceId,
  });

  return useMemo(() => {
    const listByIdsClipsById = new Map((listByIdsClipList ?? []).map((clip) => [clip.id, clip]));

    return clipIds
      .map((clipId) => reduxClipsById.get(clipId) ?? listByIdsClipsById.get(clipId))
      .filter((clip): clip is Clip => clip !== undefined);
  }, [clipIds, listByIdsClipList, reduxClipsById]);
};
